import { isAdminUser } from '@/utils/user'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Button, ButtonGroup, Circle, HStack, Icon, Menu, Portal, Text } from '@chakra-ui/react'
import { AccountSettingsTab } from '@infraloop/account-settings'
import { useTranslation } from '@infraloop/i18n'
import { LuChevronDown, LuCreditCard, LuLogOut, LuSettings, LuUsers } from 'react-icons/lu'
import { useNavigate } from 'react-router'

import useUser from '../hooks/useUser'
import { getInitials } from '../utils'

const UserMenu = () => {
  // Hooks
  const { user: auth0User, logout } = useAuth0()
  const { user } = useUser()
  const { t, language, setLanguage } = useTranslation()
  const navigate = useNavigate()

  // Derived state
  const email = auth0User?.email ?? auth0User?.name ?? ''
  const initials = getInitials(email)
  const isAdmin = isAdminUser(user)

  return (
    <Menu.Root positioning={{ placement: 'bottom-end' }}>
      <Menu.Trigger
        as="button"
        data-testid="user-menu-trigger"
        borderRadius="full"
        border="1px solid"
        borderColor="gray.200"
        bg="white"
        px={2}
        py="5px"
        _hover={{ bg: 'gray.100' }}
        _focusVisible={{ boxShadow: 'none' }}
      >
        <HStack gap="9px">
          <Circle size="30px" bg="primary.900" color="white" fontSize="12px" fontWeight="bold">
            {initials}
          </Circle>
          <Text
            display={{ base: 'none', md: 'block' }}
            fontSize="13px"
            fontWeight="semibold"
            color="gray.800"
            maxW="190px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {email}
          </Text>
          <Icon as={LuChevronDown} boxSize="15px" color="gray.400" />
        </HStack>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW="220px" borderRadius="12px" boxShadow="0 12px 32px rgba(16,24,40,.18)" p="7px">
            {/* Language switcher — the trigger already shows the email, so no header row. */}
            <Box px="12px" py="9px">
              <Text fontSize="11px" color="gray.500" mb="4px">
                {t('ui.language')}
              </Text>
              <ButtonGroup attached border="1px solid {colors.gray.200}" borderRadius="md">
                <Button
                  size="xs"
                  h="auto"
                  py="2px"
                  variant={language === 'en' ? 'solid' : 'ghost'}
                  colorPalette="blue"
                  color={language === 'en' ? 'white' : 'gray.500'}
                  _focusVisible={{ outlineColor: 'transparent' }}
                  onClick={() => setLanguage('en')}
                  data-testid="language-en"
                >
                  English
                </Button>
                <Button
                  size="xs"
                  h="auto"
                  py="2px"
                  variant={language === 'ja' ? 'solid' : 'ghost'}
                  colorPalette="blue"
                  color={language === 'ja' ? 'white' : 'gray.500'}
                  _focusVisible={{ outlineColor: 'transparent' }}
                  onClick={() => setLanguage('ja')}
                  data-testid="language-ja"
                >
                  日本語
                </Button>
              </ButtonGroup>
            </Box>
            <Menu.Separator borderColor="gray.100" />
            <Menu.Item
              value="account-settings"
              data-testid="account-settings"
              borderRadius="8px"
              fontSize="13px"
              cursor="pointer"
              onSelect={() => navigate('/user')}
            >
              <Icon as={LuSettings} boxSize="16px" color="gray.500" />
              {t('ui.accountSettings')}
            </Menu.Item>
            <Menu.Item
              value="plan"
              data-testid="plan"
              borderRadius="8px"
              fontSize="13px"
              cursor="pointer"
              onSelect={() => navigate(`/user/${AccountSettingsTab.Billing}`)}
            >
              <Icon as={LuCreditCard} boxSize="16px" color="gray.500" />
              {t('hatsuly.userMenu.plan')}
            </Menu.Item>
            {isAdmin && (
              <Menu.Item
                value="user-list"
                data-testid="user-list"
                borderRadius="8px"
                cursor="pointer"
                fontSize="13px"
                onSelect={() => navigate('/users')}
              >
                <Icon as={LuUsers} boxSize="16px" color="gray.500" />
                {t('hatsuly.userMenu.userList')}
              </Menu.Item>
            )}
            <Menu.Separator borderColor="gray.100" />
            <Menu.Item
              value="logout"
              data-testid="logout"
              borderRadius="8px"
              fontSize="13px"
              cursor="pointer"
              color="red.600"
              _hover={{ bg: 'red.50' }}
              onSelect={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            >
              <Icon as={LuLogOut} boxSize="16px" />
              {t('ui.logout')}
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

export default UserMenu
